import { useContext, useState, useRef, useEffect } from 'react';
import { useTranslation } from '../../hooks/use-translation';
import { useClassName } from '../../hooks/use-class-name';
import { useUserProfile } from '../../context/user-profile-context';
import AppPageContext from '../../context/app-page-context';
import SpriteIcon from '../shared/sprite-icon';
import { dispatch } from '../../hooks/use-bus';
import { AppBusEventType } from '../../interfaces/bus-events';
import GlobalContext from '../../context/global-context';
import OWLink from '../shared/ow-link';
import { DeleteAReviewModal } from './delete-a-review-modal';
import { useReviews } from '../../hooks/use-reviews';
import { SessionStorage } from '@ow/shared/utils/storage/session-storage';
import { kPostLoginReviewKey } from '../login/constants';
import { useAnalytics } from '../../hooks/use-analytics';
import { HeartAnimation } from './heart-animation';

// -----------------------------------------------------------------------------
export const LeaveAReview = (): JSX.Element => {
  const { t } = useTranslation();
  const { isLoggedIn, waitForLoginCheck } = useUserProfile();
  const [isCheckingLogin, setIsCheckingLogin] = useState<boolean>(true);
  const [hasCheckedLogin, setHasCheckedLogin] = useState<boolean>(false);
  const { track } = useAnalytics();
  const { app } = useContext(AppPageContext);
  const { config } = useContext(GlobalContext);
  const { getMyReviews, publishReview, deleteReview } = useReviews(
    app?.initialReviews?.comments ?? null,
  );
  const supportBaseUrl = config?.publicRuntimeConfig?.base?.supportBaseUrl;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLElement>(null);
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [reviewText, setReviewText] = useState<string>('');

  const [publishedData, setPublishedData] = useState<{
    rating: number;
    text: string;
  } | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [isAnimatingStars, setIsAnimatingStars] = useState<boolean>(false);
  const [isWriteReviewOpen, setIsWriteReviewOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showThankYou, setShowThankYou] = useState<boolean>(false);

  const applyMyReviewsToView = (myReviews): void => {
    if (!myReviews || myReviews.length === 0) {
      setPublishedData(null);
      return;
    }

    const myReview = myReviews[0];
    if (myReview?.exists === false) {
      setPublishedData(null);
      return;
    }

    const myReviewRating = Number(myReview?.rate) || 0;
    const myReviewText = myReview?.comment_content ?? '';

    setPublishedData({
      rating: myReviewRating,
      text: myReviewText,
    });
    setRating(myReviewRating);
    setReviewText(myReviewText);
  };

  useEffect(() => {
    let isCancelled = false;

    const checkLogin = async () => {
      await waitForLoginCheck();
      if (isCancelled) {
        return;
      }

      setHasCheckedLogin(true);
    };

    void checkLogin();

    return () => {
      isCancelled = true;
    };
  }, [waitForLoginCheck]);

  useEffect(() => {
    const postLoginReview = SessionStorage.get(kPostLoginReviewKey)?.asBool();
    if (isLoggedIn && postLoginReview && postLoginReview) {
      SessionStorage.remove(kPostLoginReviewKey);
      setIsWriteReviewOpen(true);
      containerRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!hasCheckedLogin) {
      return;
    }

    if (!isLoggedIn) {
      setIsCheckingLogin(false);
      return;
    }

    if (!app) {
      return;
    }

    let isCancelled = false;

    const loadMyReview = async () => {
      setIsCheckingLogin(true);
      try {
        const myReviews = await getMyReviews(app);
        if (isCancelled) {
          return;
        }
        applyMyReviewsToView(myReviews);
      } finally {
        if (!isCancelled) {
          setIsCheckingLogin(false);
        }
      }
    };

    void loadMyReview();

    return () => {
      isCancelled = true;
    };
  }, [
    hasCheckedLogin,
    isLoggedIn,
    app?.id,
    app?.alternativeStatsAppId,
    getMyReviews,
  ]);

  const previewMode = publishedData !== null && !isEditing;

  const isTextTooLong = reviewText.length > 1000;
  const isTextInvalidForLowRating =
    rating > 0 && rating <= 2 && reviewText.trim().length < 3;

  const isPublishDisabled =
    rating === 0 || isTextTooLong || isTextInvalidForLowRating || isSubmitting;

  const textAreaClassName = useClassName(
    {
      error: !previewMode && (isTextTooLong || isTextInvalidForLowRating),
      'preview-state': previewMode,
    },
    '',
    [isTextTooLong, isTextInvalidForLowRating, previewMode],
  );

  const handleLoginClick = () => {
    dispatch({
      type: AppBusEventType.App.ShowLoginModal,
      data: { show: true, source: 'userReview' },
    });
  };

  const handleCancel = () => {
    if (isEditing && publishedData) {
      setRating(publishedData.rating);
      setReviewText(publishedData.text);
      setIsEditing(false);
    } else {
      setRating(0);
      setHoverRating(0);
      setReviewText('');
      setIsWriteReviewOpen(false);
    }
  };

  const handleDisabledClick = () => {
    if (rating === 0 && !previewMode) {
      setIsAnimatingStars(true);
      setTimeout(() => setIsAnimatingStars(false), 800);
    }
  };

  const handlePublish = async () => {
    setIsSubmitting(true);
    try {
      const success = await publishReview(app, rating, reviewText);
      if (!success) {
        return;
      }

      track('appstore_reviews_published', {
        appId: app?.id,
        rating: rating.toString(),
        isEditing,
      });

      setPublishedData({
        rating,
        text: reviewText,
      });
      setIsEditing(false);
      setShowThankYou(true);
      setTimeout(() => setShowThankYou(false), 4500);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = () => {
    track('appstore_reviews_edited', {
      appId: app?.id,
      rating: rating.toString(),
    });

    setIsEditing(true);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setIsSubmitting(true);
    try {
      track('appstore_reviews_deleted', {
        appId: app.id,
      });

      const success = await deleteReview(app);
      if (!success) {
        return;
      }

      setRating(0);
      setHoverRating(0);
      setReviewText('');
      setPublishedData(null);
      setIsEditing(false);
    } finally {
      setIsSubmitting(false);
      setShowDeleteModal(false);
    }
  };

  const handlePublishClick = () => {
    handlePublish().catch(() => undefined);
  };

  const handleConfirmDelete = () => {
    confirmDelete().catch(() => undefined);
  };

  const handleSupportClicked = (supportLink: string) => {
    track('appstore_reviews_support_clicked', {
      appId: app?.id,
      linkUrl: supportLink,
    });
  };

  const onLeaveAReviewClick = () => {
    if (!isLoggedIn) {
      handleLoginClick();
    } else {
      setIsWriteReviewOpen(true);
    }

    track('appstore_reviews_cta_clicked', {
      loggedIn: isLoggedIn,
      appId: app?.id,
    });
  };

  if (isCheckingLogin) {
    return (
      <section className="leave-a-review-logged-out" ref={containerRef}>
        <div className="rotating-loader">
          <SpriteIcon icon="loader" />
        </div>
      </section>
    );
  }

  if (!isLoggedIn) {
    return (
      <section className="leave-a-review-logged-out" ref={containerRef}>
        <button
          className="btn-secondary-small icon"
          onClick={() => {
            onLeaveAReviewClick();
          }}
        >
          <SpriteIcon icon="comment" />
          {t('loginToLeaveAReview')}
        </button>
      </section>
    );
  }

  if (publishedData === null && !isWriteReviewOpen) {
    return (
      <section className="leave-a-review-logged-out" ref={containerRef}>
        <button
          className="btn-secondary-small icon"
          onClick={() => onLeaveAReviewClick()}
        >
          <SpriteIcon icon="comment" />
          {t('leaveAReview')}
        </button>
      </section>
    );
  }

  const supportLink = app?.richDetails?.supportLinkUrl || supportBaseUrl || '#';
  return (
    <section className="leave-a-review-logged-in" ref={containerRef}>
      <div className="header-row">
        <h3>
          {publishedData
            ? t('youReviewedApp', { appName: app.title })
            : t('leaveAReviewFor', { appName: app.title })}
        </h3>
        <p className="support-text">
          {t('facingIssue')}{' '}
          <OWLink
            href={`${supportLink || '#'}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => handleSupportClicked(supportLink)}
          >
            {t('support')}
          </OWLink>
        </p>
      </div>

      <div className="rating-row">
        <h4>{previewMode ? t('yourAppRate') : t('clickToRate')}</h4>
        <div
          className={`interactive-stars ${isAnimatingStars ? 'wave-anim' : ''}`}
        >
          {[1, 2, 3, 4, 5].map((star) => (
            <span
              key={star}
              className={`
                star ${
            star <= (previewMode ? rating : hoverRating || rating)
              ? 'active'
              : ''
            } ${previewMode ? 'preview' : ''}`}
              onMouseEnter={
                previewMode ? undefined : () => setHoverRating(star)
              }
              onMouseLeave={previewMode ? undefined : () => setHoverRating(0)}
              onClick={
                previewMode
                  ? undefined
                  : () => {
                    setRating(star);
                    setTimeout(() => textareaRef.current?.focus(), 0);
                  }
              }
            >
              <SpriteIcon icon="ratingStar" />
            </span>
          ))}
        </div>
      </div>

      <div className="review-input-container" onClick={handleDisabledClick}>
        {previewMode ? (
          <div className={textAreaClassName}>{reviewText.trim()}</div>
        ) : (
          <textarea
            ref={textareaRef}
            placeholder={
              rating > 0
                ? t('writeReviewOptional')
                : t('writeReviewPlaceholder')
            }
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            disabled={rating === 0 || isSubmitting}
            className={textAreaClassName}
          />
        )}
        <div className="info-row">
          <div className="validation-text">
            {!previewMode && isTextTooLong && t('reviewLimitReached')}
            {!previewMode &&
              isTextInvalidForLowRating &&
              !isTextTooLong &&
              t('reviewMinCharsRequired')}
          </div>
          <div className="char-count">
            {isTextTooLong ? `${reviewText.length} / 1000` : t('upTo1000Chars')}
          </div>
        </div>
      </div>

      <div className="actions-row">
        {previewMode ? (
          showThankYou ? (
            <span className="thank-you-message">
              {t('thankYouForReview')} <HeartAnimation />
            </span>
          ) : (
            <>
              <button
                className="clean-btn icon delete-btn"
                onClick={handleDelete}
                disabled={isSubmitting}
              >
                <SpriteIcon icon="delete" /> {t('deleteBtn')}
              </button>
              <button
                className="btn-secondary icon edit-btn"
                onClick={handleEdit}
                disabled={isSubmitting}
              >
                <SpriteIcon icon="edit" /> {t('editBtn')}
              </button>
            </>)
        ) : (
          <>
            <button
              className="btn-secondary cancel-btn"
              disabled={rating === 0 || isSubmitting}
              onClick={handleCancel}
            >
              {t('cancel')}
            </button>
            <button
              className="btn-primary publish-btn"
              disabled={isPublishDisabled}
              onClick={handlePublishClick}
            >
              {publishedData ? t('saveChanges') : t('publish')}
            </button>
          </>
        )}
      </div>

      <DeleteAReviewModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        isDeleting={isSubmitting}
      />
    </section>
  );
};
